import React from "react";
import { useNavigate } from "react-router-dom";
import { TABS } from "../lib/constants";
import { LogOut, Home } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export function Sidebar({ active, onChange, collapsed, onToggle }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const initials = (user?.name || user?.email || "U").trim().slice(0, 2).toUpperCase();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <aside
      data-testid="sidebar"
      className={`fixed lg:sticky top-0 left-0 h-[100dvh] shrink-0 z-50 flex flex-col
        border-r border-[var(--bd)] bg-[var(--bg)] lg:bg-[var(--surface)]
        overflow-x-hidden
        transition-[width,transform] duration-300 ease-out
        ${collapsed
          ? "-translate-x-full lg:translate-x-0 w-64 lg:w-16"
          : "translate-x-0 w-64 lg:w-52"
        }`}
    >
      <div className="flex-1 overflow-y-auto overflow-x-hidden hide-scrollbar px-2 py-4">


        <nav className="flex flex-col gap-1">
          {TABS.map(({ id, label, icon: Icon }) => {
            const isActive = active === id;
            return (
              <button key={id} onClick={() => onChange(id)} data-testid={`tab-${id}`} title={label}
                className={`group relative flex items-center gap-3 rounded-md py-2 sm:py-2.5 text-sm font-medium transition-colors duration-200 ${collapsed ? "justify-center px-0" : "px-2 sm:px-3"} ${
                  isActive ? "bg-[var(--accent-soft)] text-[var(--t1)]" : "text-[var(--t2)] hover:text-[var(--t1)] hover:bg-[var(--surface2)]"
                }`}>
                <span className={`absolute left-0 top-1/2 -translate-y-1/2 h-6 w-0.5 rounded-r transition-all ${isActive ? "bg-[var(--accent)]" : "bg-transparent"}`} />
                <Icon size={18} strokeWidth={2.2} className={`shrink-0 ${isActive ? "text-[var(--accent)]" : ""}`} />
                {!collapsed && <span className="truncate">{label}</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {/* User profile + actions */}
      <div className="border-t border-[var(--bd)] p-2 space-y-1" data-testid="sidebar-profile">
        <button
          onClick={() => onChange("profile")}
          title="My Profile"
          className={`w-full flex items-center gap-3 rounded-md px-2 py-2 hover:bg-[var(--surface2)] transition-colors ${collapsed ? "justify-center" : ""}`}
        >
          <div className="w-9 h-9 rounded-full overflow-hidden shrink-0 border border-[var(--bd)]" data-testid="profile-avatar">
            <img src={user?.photoURL || "/assets/profile.png"} alt="profile" className="w-full h-full object-cover object-top"
              onError={(e) => { e.target.style.display="none"; e.target.parentElement.classList.add("bg-[var(--accent)]","flex","items-center","justify-center"); e.target.parentElement.setAttribute("data-initials", initials); }} />
          </div>
          {!collapsed && (
            <div className="min-w-0 text-left">
              <div className="text-sm font-medium truncate" data-testid="profile-name">{user?.name || "User"}</div>
              <div className="text-[11px] text-[var(--t2)] truncate font-mono" data-testid="profile-email">{user?.email}</div>
            </div>
          )}
        </button>

        <button onClick={() => nav("/")} title="Back to site"
          className={`w-full flex items-center gap-3 rounded-md py-2.5 text-sm text-[var(--t2)] hover:text-[var(--t1)] hover:bg-[var(--surface2)] transition-colors ${collapsed ? "justify-center px-0" : "px-3"}`}>
          <Home size={17} className="shrink-0" />{!collapsed && <span>Back to site</span>}
        </button>

        <button onClick={handleLogout} data-testid="logout-btn" title="Log out"
          className={`w-full flex items-center gap-3 rounded-md py-2.5 text-sm text-[var(--danger)] hover:bg-[var(--danger)]/10 transition-colors ${collapsed ? "justify-center px-0" : "px-3"}`}>
          <LogOut size={17} className="shrink-0" />{!collapsed && <span>Log out</span>}
        </button>
      </div>
    </aside>
  );
}
